import React, { useEffect, useState } from 'react';
import { useActor } from '@xstate/react';
import { Seo } from '@quantum/seo';
import { AssessmentLayout } from '@/components/assessment-layout';
import { navigateToRoute } from '@/utils';
import { QuestionnaireActorType, QuestionnaireEvent, QuestionType } from '@/machines/types';
import { SeoProps } from '@/utils/types';
import { DOMAIN_LOCALE, DOMAIN_TLD } from '@/constants/domains';
import { domainFromUrl, hostUrl } from '@/helpers/domainUrl';

interface ActorProps {
  questionnaireName: string;
  actor: QuestionnaireActorType;
}

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

const keyToTitle: { [key: string]: string } = {
  prodRecInterstitialGettingStarted: 'Get started with our health quiz',
  prodRecInterstitialLifestyle: 'Lifestyle Factors',
};

export const ActorComponent: React.FC<ActorProps> = ({ actor, questionnaireName }) => {
  const [isBack, setIsBack] = useState(false);
  const seoValues = ({ link, metaTitle }: { link: string; metaTitle: string }): SeoProps => ({
    slug: 'slug',
    tld: DOMAIN_TLD[domainFromUrl()],
    node_locale: DOMAIN_LOCALE[domainFromUrl()],
    metaTitle,
    metaDescription: {
      metaDescription: 'Test recommendations for actionable insights from LetsGetChecked.',
    },
    index: false,
    autoGenerateJsonLd: false,
    canonicalUrl: `https://${hostUrl}/${link}/`,
    socialTitle: 'socialTitle',
    socialDescription: 'socialDescription',
    socialSiteName: 'socialSiteName',
    socialUrl: `https://${hostUrl}/${link}/`,
  });

  const [
    {
      context: { previousQuestions, currentQuestion, questions },
    },
    send,
  ] = useActor<QuestionnaireActorType>(actor);
  const previousQuestionsLength = previousQuestions.length;

  useEffect(() => {
    navigateToRoute(`/${questionnaireName}/${currentQuestion?.key || ''}`);
  }, [currentQuestion, questionnaireName]);

  useEffect(() => {
    const sendBack = (): void => {
      setIsBack(true);
      send({ type: QuestionnaireEvent.BACK, key: '' });
    };

    window.addEventListener('popstate', () => sendBack());

    return () => {
      window.removeEventListener('popstate', () => sendBack());
    };
  }, [send]);

  return (
    <>
      <Seo
        seoDetails={
          seoValues({
            link: `${questionnaireName}/${currentQuestion?.key || ''}`,
            metaTitle: currentQuestion ? keyToTitle[currentQuestion?.key] || currentQuestion?.text : '',
          }) as never
        }
        location={`https://${hostUrl}/${questionnaireName}/${currentQuestion?.key || ''}/`}
        pageType='pageType'
        lang={DOMAIN_LOCALE[domainFromUrl()]}
      />
      <AssessmentLayout
        displayBackButton={previousQuestionsLength !== 0}
        previousQuestionsLength={previousQuestionsLength}
        totalQuestions={questions.length}
        currentQuestion={currentQuestion}
        isPreviousSingleSelect={
          !previousQuestions[previousQuestions.length - 1]?.html &&
          !previousQuestions[previousQuestions.length - 1]?.interstitial &&
          (previousQuestions[previousQuestions.length - 1]?.questionTypeString === QuestionType.singleSelect ||
            previousQuestions[previousQuestions.length - 1]?.key === 'selfDescribedGender') &&
          !isBack
        }
        isBack={isBack}
        sendEvent={e => {
          setIsBack(false);
          setTimeout(() => {
            send(e);
          }, 0);
        }}
      />
    </>
  );
};
